<template>
    <div class="delivery">
        <h2 class="fs-16">{{ $t('deliveries.insertAddressForMoreAccurateDeliveryOptions') }}</h2>

        <UiMessage v-if="addressErrorMessage" type="error" :text="addressErrorMessage" class="mt-0 mb-3" />

        <ElementsCart2HomeDeliveryModalAddressForm
            v-model:street="street"
            v-model:city="city"
            v-model:post-code="postCode"
            @submit="formSubmitted"
        />
        <p>
            {{ $t('deliveries.desc') }}
        </p>

        <ObjectsXmasDelivery
            v-if="xmasDelivery?.isEnabled && !data?.find((del) => del.shippingFullType === 'DEL_EMAIL')"
            :guarantee="xmasDelivery?.isGuaranteed"
            :text="xmasDelivery?.message"
        />

        <div v-if="data" class="delivery-skelet table-wrapper">
            <table v-if="loading">
                <tr v-for="index in 7" :key="index">
                    <td>&nbsp;</td>
                </tr>
            </table>
            <table v-else>
                <tbody>
                    <tr v-for="(item, key) in data" :id="`delivery-${item.id}`" :key="key">
                        <td class="text-nowrap">
                            {{ item.name }}
                        </td>
                        <td class="px-0 d-none d-md-block text-center">
                            <template v-if="item.hasFreeDeliveryWithPilulkaPremium">
                                <img
                                    src="https://www.pilulka.cz/nuxt-assets/design2025/pilulka-pro-logo-green.svg"
                                    class="premium-logo"
                                    alt="Pilulka Premium"
                                />
                                <span class="text-success">{{ $t('cart.step4.free') }}</span>
                            </template>
                        </td>
                        <td class="text-right w-100">
                            <span class="text-success">{{ item.availability }}</span>
                            <br />
                            <span class="text-primary text-nowrap">{{ $formatCurrency(item.fee) }}</span>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
        <div v-else class="alert alert-danger px-5">{{ $t('error') }}</div>
    </div>
</template>

<script lang="ts" setup>
import { useI18n } from 'vue-i18n'
import ProductXmasDelivery from '~/models/Product/ProductXmasDelivery'
import { useBasketStore } from '~/stores/basket'
import ProductDelivery from '~/models/Product/ProductDelivery'

const props = defineProps<{
    productId?: number
}>()

const data = ref<ProductDelivery[]>([])
const xmasDelivery = ref<ProductXmasDelivery | null>(null)
const loading = ref(true)
const basketStore = useBasketStore()

const street = ref(basketStore.basket.customer?.invoiceAddress.street || '')
const city = ref(basketStore.basket.customer?.invoiceAddress.city || '')
const postCode = ref(basketStore.basket.customer?.invoiceAddress.postCode || '')

const addressErrorMessage = ref('')

onMounted(() => {
    fetch()

    useNuxtApp().$bus.$on('refreshDeliveries', () => {
        fetch()
    })
})

const i18n = useI18n()

const formSubmitted = async () => {
    loading.value = true

    addressErrorMessage.value = ''
    if (street.value.trim() === '') {
        addressErrorMessage.value = i18n.t('cart.step2.homeDeliveryModal.invalidStreet')
        loading.value = false

        return
    }

    if (!/\d/.test(street.value)) {
        addressErrorMessage.value = i18n.t('cart.step3.streetDoesNotContainNumber')
        loading.value = false

        return
    }

    if (city.value.trim() === '') {
        addressErrorMessage.value = i18n.t('cart.step2.homeDeliveryModal.invalidCity')
        loading.value = false

        return
    }

    if (postCode.value.trim() === '') {
        addressErrorMessage.value = i18n.t('cart.step2.homeDeliveryModal.invalidPostCode')
        loading.value = false

        return
    }
    await basketStore.setAddress(street.value, city.value, postCode.value)
    await basketStore.refreshDeliveries()
    await fetch()
}

const fetch = async (): Promise<void> => {
    if (props.productId) {
        const response = await useNuxtApp().$repository.getProductDetailData(props.productId, useUidCookie().value)
        data.value = response.product.deliveries
        xmasDelivery.value = response.product?.xmasDelivery
    } else {
        data.value = basketStore.deliveries
    }
    loading.value = false
}
</script>

<style lang="scss">
.delivery-skelet {
    table tbody {
        tr {
            td:nth-child(2) {
                .b-skeleton-text {
                    float: right;
                    max-width: 30%;
                }
            }
        }
    }
}

.modal-deliveries {
    .modal-body {
        padding: 0;
    }

    .modal-dialog {
        max-width: 550px;
    }

    &.modal {
        .modal-content {
            overflow: hidden;
            box-shadow: none;
        }

        .modal-title {
            font-size: 16px;
        }
    }

    .modal-content {
        .table-wrapper {
            margin: 0 -1.5em -1.5em;

            td {
                border-width: 0;
            }
        }

        table {
            width: 100%;
            font-weight: 500;
            margin-bottom: 10px;
            @media (max-width: 768px) {
                display: block;
            }

            tr {
                background-color: $color-grey;
            }

            tr:nth-child(even) {
                background-color: transparent;
            }

            td {
                padding: 0.75em 1.5em;

                @media (max-width: 480px) {
                    margin-bottom: -1.5em;
                    padding: 0.75em 1.5em;
                }
            }

            @media (max-width: 768px) {
                tr {
                    display: block;
                }

                tbody {
                    display: block;
                }
            }
        }
    }
}

.premium-logo {
    width: 100px;
    max-width: none;
}
</style>
